import React, {useEffect, useState} from 'react'
import {DiaryStyle} from './DiaryStyle'
import {Select} from 'antd'
import {Snackbar, Typography} from '@mui/material'
import {FormattedMessage} from 'react-intl'
import BoxAderenza from 'src/components/elements/BoxAderenza/BoxAderenza'
import TableCustom, {PaginationType, TableColumn} from 'src/components/elements/TableCustom/TableCustom'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import Icon from '@mdi/react'
import {mdiEye, mdiDownloadCircleOutline, mdiDownload} from '@mdi/js'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {PATHS, STATUS} from 'src/constants/constants'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import {AdherenceType} from 'src/pages/home/Home'
import FileViewerModal from 'src/components/elements/FileViewerModal/FileViewerModal'
import Filters, {FilterItem} from '../../Filters/Filters'

type DiaryProps = {
  patientId: string,
  serviceConf: string,
  userFirstName: string,
  userLastName: string,
  userCode: string
};

const assumptionsStateOptions = [
  {
    value: 'COMPLETED',
    label: 'Effettuata'
  },
  {
    value: 'PLANNED',
    label: 'Pianificata'
  },
  {
    value: 'TO_BE_PLANNED',
    label: 'In pianificazione'
  },
  {
    value: 'CANCELLED',
    label: 'Non effettuata'
  }
]

const Diary: React.FC<DiaryProps> = ({patientId, serviceConf, userFirstName, userLastName, userCode}) => {
  const {diaryConfigurations: {diaryType, columns = [], tableTitle = '', showExport, showTherapiesFilter, adherenceBox}} = useAppSelector(state => state.pspConfigReducer.data.patientDetailConfiguration ?? {})
  const [data, setData] = useState<Record<string, any>[]>([])
  const [filters, setFilters] = useState<any>([])
  const [status, setStatus] = useState<string>()
  const [pagination, setPagination] = useState<PaginationType>({
    page: 0,
    rowsPerPage: 10,
    order: ''
  })
  const [fileName, setFileName] = useState<any>('Diario')
  const [count, setCount] = useState<number>(0)
  const [statusCount, setStatusCount] = useState<string>()
  const [patientAdherence, setPatientAdherence] = useState<AdherenceType>()
  const [statusAdherence, setStatusAdherence] = useState<string>()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    const fetchAdherence = async () => {
      const fetchedData = await apiClient.getPatientAdherence(patientId, setStatusAdherence)
      setPatientAdherence(fetchedData)
    }

    fetchAdherence()
  }, [columns, patientId])

  useEffect(() => {
    const fetchData = async () => {
      if (diaryType === 'DIARY_MORISKY') {
        const fetchedData = await apiClient.getPatientDiaryMorisky(patientId, {...pagination}, setStatus, filters) ?? []
        setData(fetchedData.map((item: any, i: number) => ({...item, _id: item._id ?? i})))
        const fetchedDataCount = await apiClient.getPatientDiaryMoriskyCount(patientId, {}, setStatusCount, filters) ?? 0
        setCount(fetchedDataCount)
      } else {
        const fetchedData = await apiClient.getPatientsAssumptions(patientId, setStatus, pagination, filters) ?? []
        const parsedData = fetchedData.map((assumption: any, i: number) => {
          return {
            ...assumption,
            _id: assumption._id ?? i,
            state: assumptionsStateOptions.find((option) => option.value === assumption.state)?.label
          }
        })
        setData(parsedData)
        const fetchedDataCount = await apiClient.getPatientsAssumptionsCount(patientId, setStatusCount, filters) ?? 0
        setCount(fetchedDataCount)
      }
    }

    fetchData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, filters])

  const getTherapy = () => {
    if (data.length > 0) {
      return {
        label: data[0].drug,
        value: data[0].drug
      }
    }
  }

  const filterConfig: FilterItem[] = [
    {
      id: 'date',
      label: 'Data',
      type: 'rangePicker'
    }
  ]

  const onSubmitFilters = (newFilters: any) => {
    setFilters(newFilters)
    setPagination(prev => ({...prev, page: 0}))
  }

  const [file, setFile] = useState<any>()
  const [openViewModal, setOpenViewModal] = useState<boolean>(false)
  const [statusDownload, setStatusDownload] = useState<string>()
  const [pendingModalOpen, setPendingModalOpen] = useState<boolean>(false)

  useEffect(() => {
    if (pendingModalOpen && file && statusDownload === STATUS.FULFILLED) {
      setOpenViewModal(true)
      setPendingModalOpen(false)
    }
  }, [file, statusDownload, pendingModalOpen])

  const handleModalView = async (row: any) => {
    setOpenViewModal(true)
    const fileResponse = await apiClient.generatePdf(`${new URL(window.location.href).origin}/${PATHS.PDF_DIARY}?filters=${JSON.stringify({patientId})}`, setStatusDownload)
    const blob = new Blob([new Uint8Array(fileResponse)], {type: 'application/pdf'})
    setFile((window.URL || window.webkitURL).createObjectURL(blob))
    setFileName(`Diario - ${userFirstName} ${userLastName}`)
  }

  const exportDiaryExcel = async () => {
    const response = await apiClient.downloadDiaryExcel(patientId, setStatusDownload, filters)

    const url = window.URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `Diario_${patientId}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const onPreviewAssumption = async (assumption: any) => {
    setIsLoading(true)
    const response = await apiClient.exportPatientAssumption(patientId, assumption._id, setStatusDownload)
    setIsLoading(false)

    if (response) {
      const assumptionDate = new Date(assumption.date).toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '-')
      const downloadFileName = `Scheda_infusionale_${userCode}_${assumptionDate}.pdf`

      const blob = new Blob([response.data], {type: 'application/pdf'})
      const filepath = URL.createObjectURL(blob)

      const newWindow = window.open(`${filepath}#toolbar=0`, '_blank')

      if (newWindow) {
        newWindow.document.title = downloadFileName
      }
    }
  }

  const onExportAssumption = async (assumption: any) => {
    setIsLoading(true)
    const response = await apiClient.exportPatientAssumption(patientId, assumption._id, setStatusDownload)
    setIsLoading(false)

    if (response) {
      const assumptionDate = new Date(assumption.date).toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '-')
      const downloadFileName = `Scheda_infusionale_${userCode}_${assumptionDate}.pdf`

      const blob = new Blob([response.data], {type: 'application/pdf'})
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = downloadFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }

  return (
    <DiaryStyle>
      {adherenceBox &&
        <div className='card header'>
          <div>
            <Typography variant='body1'>
              <FormattedMessage id='therapy' />
            </Typography>
            <Select
              disabled
              value={getTherapy()}
            />
          </div>
          <div style={{display: 'flex'}}>
            <BoxAderenza subtitle='Aderenza infusioni tutor' title={patientAdherence?.pspAdherencePercentage ? patientAdherence?.pspAdherencePercentage + '%' : '-'} />
            <BoxAderenza subtitle='Aderenza complessiva' title={patientAdherence?.totalAdherencePercentage ? patientAdherence?.totalAdherencePercentage + '%' : '-'} />
          </div>
        </div>}
      <FileViewerModal
        MIMETypeValue={'application/pdf'}
        fileName={fileName}
        filepath={file}
        handleClose={() => { setOpenViewModal(false); setFile(undefined); setFileName(undefined) }}
        open={openViewModal}
        status={statusDownload}
      />
      <div style={{display: 'flex', justifyContent: 'flex-end', gap: '16px', padding: '14px'}}>
        {
          showTherapiesFilter && (
            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
              <Typography>{'Terapia:'}</Typography>
              <Select
                disabled
                getPopupContainer={(trigger) => trigger}
                options={[
                  {
                    label: 'Cerdelga',
                    value: 'Cerdelga'
                  }
                ]}
                value={'Cerdelga'}
              />
            </div>
          )
        }
      </div>
      <div className='card' style={{display: 'flex', flexDirection: 'column'}}>
        <div style={{padding: '25px 25px 40px', border: '1px solid #EEE', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Typography variant='h4'>{tableTitle}</Typography>
          <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
            <Filters config={filterConfig} key='diary-filters' onSubmit={onSubmitFilters} />
            {showExport && (
              <PrimaryButton
                onClick={diaryType === 'DIARY_DRUGS' ? exportDiaryExcel : handleModalView}
                startIcon={<Icon path={mdiDownloadCircleOutline} size={1} />}
              >{'Esporta'}
              </PrimaryButton>
            )}
          </div>
        </div>
        <TableCustom
          actions={diaryType === 'DIARY_DRUGS' ? [
            {
              id: 'preview',
              label: 'Visualizza',
              icon: mdiEye,
              action: onPreviewAssumption
            },
            {
              id: 'download',
              label: 'Download',
              icon: mdiDownload,
              action: onExportAssumption
            }
          ] : []}
          columns={columns.filter((col: TableColumn) => (col.id !== 'adherence' || serviceConf === 'EXTRA'))}
          isLoading={isLoading || status === STATUS.PENDING}
          onPaginationChange={setPagination}
          pagination={pagination}
          rows={data}
          totalElements={count}
        />
      </div>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        autoHideDuration={2000}
        onClose={() => { setStatus(undefined); setStatusCount(undefined) }}
        open={status === STATUS.REJECTED || statusCount === STATUS.REJECTED || statusAdherence === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom
            onClose={() => { setStatus(undefined); setStatusCount(undefined); setStatusAdherence(undefined) }}
            severity='error'
            sx={{width: '100%'}}
          >
            <Typography variant='h5'>
              {'Errore nel caricare i dati.'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </DiaryStyle>
  )
}

export default Diary
